"use client";

import { Skeleton } from "../ui";
import { Button } from "../ui/button";
import { CardHeader, CardTitle } from "../ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Separator } from "../ui/separator";
import { useAsyncState } from "@erp/shared-libs/hooks";
import { cn } from "@erp/shared-libs/utils";
import { ChevronDown } from "lucide-react";
import {
  Children,
  createContext,
  Dispatch,
  FC,
  isValidElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from "react";

export type CollapseGroup = {
  key: string;
  label: string;
  isCollapsed?: boolean;
  hidden?: boolean;
};

interface CollapseGroupProps {
  groups: CollapseGroup[];
  setGroups: Dispatch<SetStateAction<CollapseGroup[]>>;
  isLoading?: boolean;
}

const collapseGroupContext = createContext<CollapseGroupProps>({
  groups: [],
  setGroups: () => {},
  isLoading: false,
});

const useCollapseGroupContext = () => {
  const context = useContext(collapseGroupContext);

  return {
    ...context,
  };
};

export const CollapseGroupProvider = (props: {
  currentGroup?: string;
  groups: CollapseGroup[];
  children: React.ReactNode;
  isLoading?: boolean;
}) => {
  const [groups, setGroups] = useAsyncState<CollapseGroup[]>(props.groups);

  return (
    <collapseGroupContext.Provider value={{ groups, setGroups, isLoading: props.isLoading }}>
      {props.children}
    </collapseGroupContext.Provider>
  );
};

export const CollapseGroupBar = () => {
  const { groups, setGroups, isLoading } = useCollapseGroupContext();
  const [currentGroup, setCurrentGroup] = useAsyncState<string>(
    groups.filter((group) => !group.hidden)[0]?.key
  );

  const handleValueChange = useCallback(
    (value: string) => {
      setGroups((prev) => {
        const newGroups = prev.map((group) => {
          if (group.key === value) {
            return { ...group, isCollapsed: false };
          }
          return { ...group, isCollapsed: true };
        });
        return newGroups;
      });

      setCurrentGroup(value);
    },
    [setGroups, setCurrentGroup]
  );

  if (isLoading) {
    return <Skeleton className="h-8 w-48" />;
  }

  return (
    <RadioGroup
      value={currentGroup}
      className="border-input bg-on-background flex gap-0 overflow-x-auto rounded-lg border"
      onValueChange={handleValueChange}
    >
      {groups.map(({ key, label, hidden }) => {
        if (hidden) return null;
        return (
          <div key={key} className="group flex items-center">
            <RadioGroupItem value={key} id={key} className="peer hidden" />
            <Label
              htmlFor={key}
              className="peer-data-[state=checked]:bg-foreground peer-data-[state=checked]:text-on-background cursor-pointer
                whitespace-nowrap border-l p-2 group-first:border-l-0"
            >
              {label}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
};

export const CollapseGroup: FC<{
  defaultOpen?: boolean;
  groupKey: string;
  children: React.ReactNode;
  rootClassName?: string;
  className?: string;
}> = ({ groupKey, children, defaultOpen, rootClassName, className }) => {
  const { groups, setGroups } = useCollapseGroupContext();

  const currentGroup = groups.find((group) => group.key === groupKey);

  const handleSetCurrentGroup = useCallback(
    (open: boolean) => {
      setGroups((prev) => {
        const newGroups = prev.map((group) => {
          if (group.key === groupKey) {
            return { ...group, isCollapsed: !open };
          }
          return { ...group };
        });
        return newGroups;
      });
    },
    [groupKey, setGroups]
  );

  const titleChild = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === CollapseGroupTitle
  );

  const otherChildren = Children.toArray(children).filter(
    (child) => !isValidElement(child) || child.type !== CollapseGroupTitle
  );

  return (
    <Collapsible
      defaultOpen={defaultOpen ?? false}
      open={!currentGroup?.isCollapsed}
      onOpenChange={handleSetCurrentGroup}
      className={cn(rootClassName)}
    >
      <CollapseGroupTrigger groupKey={groupKey} titleChild={titleChild} />

      <CollapsibleContent className={cn(className)}>
        <Separator />
        {otherChildren}
      </CollapsibleContent>
    </Collapsible>
  );
};

const CollapseGroupTrigger = ({
  groupKey,
  titleChild,
}: {
  groupKey: string;
  titleChild?: ReactNode;
}) => {
  const { groups } = useCollapseGroupContext();

  const title = useMemo(
    () => groups.find((group) => group.key === groupKey)?.label,
    [groups, groupKey]
  );

  return (
    <CollapsibleTrigger asChild>
      <Button className="w-full rounded-none" variant="ghost">
        <CardHeader className="p-0 sm:p-0">
          <CardTitle>{titleChild ?? title}</CardTitle>
        </CardHeader>
        <ChevronDown className="ml-auto" />
      </Button>
    </CollapsibleTrigger>
  );
};

export const CollapseGroupTitle = ({ children }: { children: React.ReactNode }) => {
  return children;
};
